import { useTeams, type Team } from '@/hooks/useTeams';
import { useUser } from '@/hooks/useUser';
import { createContext } from '@/utils/context';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

interface TeamContextType {
  teams: Team[];
  activeTeam: Team | null;
  setActiveTeam: (team: Team | null) => Promise<void>;
  isChangingTeam: boolean;
}

const [useTeamContext, TeamContextProvider] =
  createContext<TeamContextType>('TeamContext');

export const TeamProvider = ({ children }: PropsWithChildren) => {
  const queryClient = useQueryClient();
  const { data: teams } = useTeams();
  const { data: user, updateUser } = useUser();
  const [activeTeam, setActiveTeamState] = useState<Team | null>(null);
  const [isChangingTeam, setIsChangingTeam] = useState(false);

  // Set active team from user's active_team_id
  useEffect(() => {
    if (teams && user?.active_team_id) {
      const activeTeamFromUser = teams.find(
        (team) => team.id === user.active_team_id,
      );
      if (activeTeamFromUser) {
        setActiveTeamState(activeTeamFromUser);
      }
    }
  }, [teams, user?.active_team_id]);

  const handleSetActiveTeam = useCallback(
    async (newTeam: Team | null) => {
      if (!newTeam) return;

      try {
        setIsChangingTeam(true);
        await updateUser.mutateAsync({ active_team_id: newTeam.id });
        queryClient.clear();
        setActiveTeamState(newTeam);
      } catch (error) {
        console.error('Failed to update active team:', error);
      } finally {
        setIsChangingTeam(false);
      }
    },
    [updateUser, queryClient],
  );

  const value = useMemo(
    () => ({
      teams: teams ?? [],
      activeTeam,
      setActiveTeam: handleSetActiveTeam,
      isChangingTeam,
    }),
    [teams, activeTeam, handleSetActiveTeam, isChangingTeam],
  );

  return <TeamContextProvider value={value}>{children}</TeamContextProvider>;
};
export { useTeamContext };
